/**
 * Imputación: the pure rules behind the personal tab.
 *
 * Everything here is free of React and I/O so it can be unit-tested on its own
 * (imputacionLogic.test.ts). The server is the authority — it re-validates every
 * cell and refuses what does not fit — but the screen applies the same rules
 * first, so the person sees a problem next to the cell instead of in a result
 * list after a round trip to Redmine.
 *
 * A draft holds ABSOLUTE targets per (line, day), never deltas: the server
 * compares each target with a fresh read of what is logged, so sending the same
 * draft twice changes nothing.
 */
import type {
    ImputacionDay,
    ImputacionLine,
    ImputacionLineKind,
    ImputacionMonth,
    ImputacionTotals,
    ImputacionLevel,
    IsoDate,
    SubmitCell,
    SubmitResult,
} from '../../../types/imputacion'

/** lineKey → ISO day → target hours. */
export type Draft = Record<string, Record<IsoDate, number>>

/** Two hour figures closer than this are the same figure (float noise). */
const EPS = 0.001
/** A day is "done" when it is this close to its expected hours (as on the server). */
export const DAY_TOLERANCE = 0.25
/** Before this Madrid hour today is not offered as fillable: the day is not over. */
export const TODAY_FILL_HOUR = 15
/** Redmine accepts any hours, but this instance logs quarter hours. */
const STEP = 0.25
const MAX_DAY_HOURS = 24
/** One ISO week per request keeps a submit far below the 90 s client timeout. */
export const MAX_CELLS_PER_CHUNK = 40
const DRAFT_TTL_MS = 12 * 60 * 60 * 1000

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const round2 = (n: number): number => Math.round(n * 100) / 100

/** 8.5 → "8,5"; 150 → "150"; 2.25 → "2,25". Spanish decimal comma, no trailing zeros. */
export function fmtNum(n: number): string {
    const r = round2(Number.isFinite(n) ? n : 0)
    return String(Object.is(r, -0) ? 0 : r).replace('.', ',')
}

/** "8,5 h" */
export const fmtH = (n: number): string => `${fmtNum(n)} h`

/** "+6" / "−2,5" with a real minus sign, for differences. */
export function fmtSigned(n: number): string {
    const r = round2(n)
    if (r > 0) return `+${fmtNum(r)}`
    if (r < 0) return `−${fmtNum(-r)}`
    return '0'
}

/** Parses what a person types in a cell: "8,5", "8.5", " 6 ". Empty → null; junk → NaN. */
export function parseHours(text: string): number | null {
    const t = text.trim().replace(',', '.')
    if (t === '') return null
    if (!/^-?\d*(\.\d*)?$/.test(t) || t === '.' || t === '-') return NaN
    return Number(t)
}

const MONTHS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]
const WEEKDAYS = ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá']

export const monthName = (m: number): string => MONTHS[(m - 1 + 12) % 12]

/** Dates are plain calendar days: parse at UTC noon so no timezone moves them. */
const toDate = (iso: IsoDate): Date => new Date(`${iso}T12:00:00Z`)

/** "03/09" */
export const ddmm = (iso: IsoDate): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`
/** "lu" */
export const weekdayShort = (iso: IsoDate): string => WEEKDAYS[toDate(iso).getUTCDay()]
/** "lu 14" */
export const dayLabel = (iso: IsoDate): string => `${weekdayShort(iso)} ${Number(iso.slice(8, 10))}`

/** Adds months to a (year, month) pair. */
export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
    const idx = year * 12 + (month - 1) + delta
    return { year: Math.floor(idx / 12), month: (idx % 12) + 1 }
}

/** Monday of the ISO week of a day, as the grouping key for chunks. */
export function isoWeekStart(iso: IsoDate): IsoDate {
    const d = toDate(iso)
    const dow = (d.getUTCDay() + 6) % 7 // Monday = 0
    d.setUTCDate(d.getUTCDate() - dow)
    return d.toISOString().slice(0, 10)
}

/** Link that opens one day of the person's own entries in Redmine. */
export function redmineDayUrl(redmineUrl: string | null, iso: IsoDate): string | null {
    if (!redmineUrl) return null
    return `${redmineUrl.replace(/\/+$/, '')}/time_entries?user_id=me&from=${iso}&to=${iso}`
}

// ---------------------------------------------------------------------------
// Month helpers
// ---------------------------------------------------------------------------

const KIND_ORDER: Record<ImputacionLineKind, number> = {
    planned: 0,
    default: 1,
    needs_choice: 1,
    unplanned: 2,
    hidden: 3,
    absence: 4,
}

/** Planned first, then "rest of the day", unplanned, and absences always last. */
export function orderedLines(month: ImputacionMonth): ImputacionLine[] {
    return month.lines
        .map((line, i) => ({ line, i }))
        .sort((a, b) => KIND_ORDER[a.line.kind] - KIND_ORDER[b.line.kind] || a.i - b.i)
        .map(({ line }) => line)
}

export const findDay = (month: ImputacionMonth, iso: IsoDate): ImputacionDay | undefined =>
    month.days.find((d) => d.date === iso)

export const absenceLine = (month: ImputacionMonth): ImputacionLine | undefined =>
    month.lines.find((l) => l.kind === 'absence')

/** Grid columns: working days, plus any other day that already has hours on it. */
export const visibleDays = (month: ImputacionMonth): ImputacionDay[] =>
    month.days.filter((d) => d.kind === 'working' || d.logged_hours > EPS)

const val = (rec: Record<IsoDate, number> | undefined, iso: IsoDate): number => rec?.[iso] ?? 0

/** The person-visible "Resto de jornada" for the default filler; the plan label otherwise. */
export function sourceLabel(line: ImputacionLine): string {
    if (line.sources.some((s) => s.is_default)) return 'Resto de jornada'
    return line.sources[0]?.label ?? line.label
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/** What the cell holds now: the draft target, else what is logged. */
export function cellValue(line: ImputacionLine, iso: IsoDate, draft: Draft): number {
    const d = draft[line.key]?.[iso]
    return d !== undefined ? d : val(line.logged, iso)
}

export const hasDraft = (draft: Draft, key: string, iso: IsoDate): boolean => draft[key]?.[iso] !== undefined

/**
 * A ghost is the suggested target shown in grey. Only on days that still lack
 * hours (a complete or covered day suggests nothing), on lines that can be
 * logged, and only where the suggestion adds something.
 */
export function showGhost(line: ImputacionLine, day: ImputacionDay, draft: Draft): boolean {
    if (!(day.editable || day.state === 'future')) return false
    if (day.chip === 'completo' || day.chip === 'cubierto') return false
    if (!line.loggable) return false
    if (hasDraft(draft, line.key, day.date)) return false
    return val(line.suggested, day.date) > val(line.logged, day.date) + EPS
}

/** Hours the recommendation would add on a day, over the loggable lines. */
export function dayFillHours(month: ImputacionMonth, iso: IsoDate): number {
    let sum = 0
    for (const line of month.lines) {
        if (!line.loggable) continue
        sum += Math.max(0, val(line.suggested, iso) - val(line.logged, iso))
    }
    return round2(sum)
}

const isCovered = (day: ImputacionDay) => day.chip === 'completo' || day.chip === 'cubierto'

/**
 * Days "Completar lo recomendado" can fill: editable, not covered, already due
 * (today only when asked) and with more than a quarter hour still to add.
 */
export function fillableDays(month: ImputacionMonth, includeToday: boolean): IsoDate[] {
    return month.days
        .filter((d) => d.editable && !isCovered(d) && d.state !== 'future')
        .filter((d) => includeToday || d.state !== 'today')
        .filter((d) => dayFillHours(month, d.date) > DAY_TOLERANCE)
        .map((d) => d.date)
}

/** Editable, due days that are not complete: where a missing project choice matters. */
export function pendingDays(month: ImputacionMonth, includeToday: boolean): IsoDate[] {
    return month.days
        .filter((d) => d.editable && !isCovered(d) && d.state !== 'future' && d.kind === 'working')
        .filter((d) => includeToday || d.state !== 'today')
        .filter((d) => d.missing_hours > DAY_TOLERANCE || dayFillHours(month, d.date) > DAY_TOLERANCE)
        .map((d) => d.date)
}

/** Lines still waiting for a project that the recommendation needs on those days. */
export function blockingLines(month: ImputacionMonth, days: IsoDate[]): ImputacionLine[] {
    return month.lines.filter(
        (l) => l.kind === 'needs_choice' && days.some((d) => val(l.suggested, d) > val(l.logged, d) + EPS),
    )
}

/** Whether today counts by default: only from 15:00 Madrid, once most of the day is done. */
export const todayIncludedByDefault = (month: ImputacionMonth): boolean => month.madrid_hour >= TODAY_FILL_HOUR

/** The recommended targets for the given days, as a draft. */
export function buildFillDraft(month: ImputacionMonth, days: IsoDate[]): Draft {
    const draft: Draft = {}
    for (const iso of days) {
        for (const line of month.lines) {
            if (!line.loggable) continue
            const target = val(line.suggested, iso)
            if (target > val(line.logged, iso) + EPS) {
                draft[line.key] = { ...(draft[line.key] ?? {}), [iso]: round2(target) }
            }
        }
    }
    return draft
}

/** Per-line hours a fill adds on one day, for the confirmation list. */
export function fillBreakdown(month: ImputacionMonth, iso: IsoDate): { line: ImputacionLine; hours: number }[] {
    return orderedLines(month)
        .filter((l) => l.loggable)
        .map((line) => ({ line, hours: round2(val(line.suggested, iso) - val(line.logged, iso)) }))
        .filter((x) => x.hours > EPS)
}

/** Whether "Estuve ausente" can be offered: there is a loggable absence line. */
export const canMarkAbsent = (month: ImputacionMonth): boolean => {
    const a = absenceLine(month)
    return Boolean(a && a.loggable && a.redmine_project_id)
}

/** Removes every draft of a day, except on the given line. */
function clearDayDrafts(draft: Draft, iso: IsoDate, keepKey?: string): Draft {
    const next: Draft = {}
    for (const [key, cells] of Object.entries(draft)) {
        if (key === keepKey || cells[iso] === undefined) {
            next[key] = cells
            continue
        }
        const { [iso]: _dropped, ...rest } = cells
        if (Object.keys(rest).length) next[key] = rest
    }
    return next
}

/**
 * Absence target for "Estuve ausente": what the day still lacks goes to the
 * absence line. On an empty day that is the whole expected day; on a partial
 * day the hours already logged elsewhere stay (they were worked), so writing
 * the full expected hours on top would over-log the day.
 */
export function absenceTargetForDay(month: ImputacionMonth, iso: IsoDate): number {
    const absence = absenceLine(month)
    const day = findDay(month, iso)
    if (!absence || !day) return 0
    return round2(val(absence.logged, iso) + Math.max(0, day.expected_hours - day.logged_hours))
}

/**
 * "Estuve ausente": the rest of the day goes to the absence line and whatever
 * else the draft held for that day is dropped.
 */
export function absenceDraftForDay(month: ImputacionMonth, iso: IsoDate, draft: Draft = {}): Draft {
    const absence = absenceLine(month)
    const day = findDay(month, iso)
    if (!absence || !day) return draft
    const next = clearDayDrafts(draft, iso, absence.key)
    next[absence.key] = { ...(next[absence.key] ?? {}), [iso]: absenceTargetForDay(month, iso) }
    return next
}

/**
 * Sets one cell. A target equal to what is logged is not a change and leaves
 * the draft; a full absence on a day clears the other lines' drafts of that day.
 */
export function applyCellChange(
    month: ImputacionMonth,
    draft: Draft,
    key: string,
    iso: IsoDate,
    value: number | null,
): Draft {
    const line = month.lines.find((l) => l.key === key)
    if (!line) return draft
    let next: Draft = { ...draft }
    const cells = { ...(next[key] ?? {}) }
    if (value === null || Math.abs(value - val(line.logged, iso)) <= EPS) {
        delete cells[iso]
    } else {
        cells[iso] = round2(value)
    }
    if (Object.keys(cells).length) next[key] = cells
    else delete next[key]
    const day = findDay(month, iso)
    if (line.kind === 'absence' && day && value !== null && day.expected_hours > 0 && value >= day.expected_hours - 0.01) {
        next = clearDayDrafts(next, iso, key)
    }
    return next
}

/** A day the absence line (draft or logged) already fills: the other inputs are greyed out. */
export function dayCoveredByAbsence(month: ImputacionMonth, iso: IsoDate, draft: Draft): boolean {
    const absence = absenceLine(month)
    const day = findDay(month, iso)
    if (!absence || !day || day.expected_hours <= 0) return false
    return cellValue(absence, iso, draft) >= day.expected_hours - 0.01
}

/** Logged plus draft, over every line, for the "Total día" row. */
export function dayTotal(month: ImputacionMonth, iso: IsoDate, draft: Draft): number {
    // day.logged_hours also counts entries on lines this view does not list
    // (none in own mode); the per-line sum is what the person edits.
    let sum = 0
    for (const line of month.lines) sum += cellValue(line, iso, draft)
    return round2(sum)
}

export type CellIssue = { kind: 'floor' | 'step' | 'range'; message: string }

/**
 * Checks one target before it is sent, with the server's own rules:
 * - never below the hours typed directly in Redmine (the Manager does not edit those);
 * - the CHANGE is a multiple of a quarter hour, so an odd value already logged
 *   (1,33) can be left as it is;
 * - 0 to 24 h.
 */
export function validateCell(line: ImputacionLine, iso: IsoDate, value: number): CellIssue | null {
    if (!Number.isFinite(value)) return { kind: 'range', message: 'Escribe un número de horas.' }
    if (value < 0) return { kind: 'range', message: 'Las horas no pueden ser negativas.' }
    if (value > MAX_DAY_HOURS) return { kind: 'range', message: 'Máximo 24 h por día.' }
    const locked = val(line.locked_hours, iso)
    if (value < locked - 0.01) {
        return {
            kind: 'floor',
            message: `No puedes bajar de ${fmtH(locked)}: se imputaron directamente en Redmine el ${ddmm(iso)}.`,
        }
    }
    const delta = value - val(line.logged, iso)
    if (Math.abs(delta) > EPS && Math.abs(delta / STEP - Math.round(delta / STEP)) > 1e-6) {
        return { kind: 'step', message: 'Usa cuartos de hora (0,25).' }
    }
    return null
}

export interface DraftIssue {
    lineKey: string
    date: IsoDate
    issue: CellIssue
}

/** Every draft cell that would be refused, for the inline messages and the send button. */
export function draftIssues(month: ImputacionMonth, draft: Draft): DraftIssue[] {
    const out: DraftIssue[] = []
    for (const line of orderedLines(month)) {
        for (const [iso, value] of Object.entries(draft[line.key] ?? {}).sort()) {
            const issue = validateCell(line, iso, value)
            if (issue) out.push({ lineKey: line.key, date: iso, issue })
        }
    }
    return out
}

// ---------------------------------------------------------------------------
// Submit
// ---------------------------------------------------------------------------

/**
 * One cell per (day, project) for every changed cell of a loggable line on an
 * editable day. Lines are unique per Redmine project by construction (the
 * server merges plan rows that share one), so nothing needs adding up.
 */
export function toSubmitCells(month: ImputacionMonth, draft: Draft): SubmitCell[] {
    const editable = new Set(month.days.filter((d) => d.editable).map((d) => d.date))
    const cells: SubmitCell[] = []
    for (const line of month.lines) {
        if (!line.loggable || line.redmine_project_id === null) continue
        for (const [iso, hours] of Object.entries(draft[line.key] ?? {})) {
            if (!editable.has(iso)) continue
            if (Math.abs(hours - val(line.logged, iso)) <= EPS) continue
            cells.push({
                spent_on: iso,
                project_id: line.redmine_project_id,
                hours: round2(hours),
                activity_id: line.activity_id,
            })
        }
    }
    return cells.sort((a, b) => a.spent_on.localeCompare(b.spent_on) || a.project_id - b.project_id)
}

/**
 * Groups cells by ISO week, in date order. A week with more than `max` cells is
 * split, but a day is never cut in two: the server's per-day guard compares the
 * whole day.
 */
export function chunkByWeek(cells: SubmitCell[], max: number = MAX_CELLS_PER_CHUNK): SubmitCell[][] {
    const sorted = [...cells].sort((a, b) => a.spent_on.localeCompare(b.spent_on) || a.project_id - b.project_id)
    const weeks = new Map<IsoDate, SubmitCell[]>()
    for (const c of sorted) {
        const k = isoWeekStart(c.spent_on)
        weeks.set(k, [...(weeks.get(k) ?? []), c])
    }
    const chunks: SubmitCell[][] = []
    for (const week of weeks.values()) {
        if (week.length <= max) {
            chunks.push(week)
            continue
        }
        const byDay = new Map<IsoDate, SubmitCell[]>()
        for (const c of week) byDay.set(c.spent_on, [...(byDay.get(c.spent_on) ?? []), c])
        let current: SubmitCell[] = []
        for (const dayCells of byDay.values()) {
            if (current.length && current.length + dayCells.length > max) {
                chunks.push(current)
                current = []
            }
            current = [...current, ...dayCells]
        }
        if (current.length) chunks.push(current)
    }
    return chunks
}

/** Per day, the logged total a draft was built on (see `rebaseSeen`). */
export type SeenTotals = Record<IsoDate, number>

/**
 * What the person saw logged on each touched day. If Redmine changed in the
 * meantime the server answers 'conflict' for that day instead of writing.
 * `basis` (the totals the draft cells were typed against) wins over the month
 * on screen: a re-read in between (a project choice, a restored draft) must
 * not silently absorb an edit made in Redmine after the cells were typed.
 */
export function seenDayTotals(month: ImputacionMonth, cells: SubmitCell[], basis: SeenTotals = {}): SeenTotals {
    const out: SeenTotals = {}
    for (const c of cells) {
        const seen = basis[c.spent_on]
        out[c.spent_on] = round2(seen !== undefined ? seen : (findDay(month, c.spent_on)?.logged_hours ?? 0))
    }
    return out
}

/**
 * The basis of a draft: for every day that has draft cells, the day total the
 * first of them was typed against. Days already in `seen` keep their value;
 * new days take the month on screen; days that left the draft are dropped.
 */
export function rebaseSeen(month: ImputacionMonth, draft: Draft, seen: SeenTotals): SeenTotals {
    const out: SeenTotals = {}
    for (const cells of Object.values(draft)) {
        for (const iso of Object.keys(cells)) {
            if (out[iso] !== undefined) continue
            out[iso] = seen[iso] !== undefined ? seen[iso] : round2(findDay(month, iso)?.logged_hours ?? 0)
        }
    }
    return out
}

/** "{n} cambios · +{a} h / −{r} h" */
export function draftStats(month: ImputacionMonth, draft: Draft): { changes: number; added: number; removed: number } {
    let added = 0
    let removed = 0
    const cells = toSubmitCells(month, draft)
    for (const c of cells) {
        const line = month.lines.find((l) => l.redmine_project_id === c.project_id)
        const logged = line ? val(line.logged, c.spent_on) : 0
        if (c.hours > logged) added += c.hours - logged
        else removed += logged - c.hours
    }
    return { changes: cells.length, added: round2(added), removed: round2(removed) }
}

const cellId = (iso: IsoDate, projectId: number) => `${iso}|${projectId}`

/** Line key of a Redmine project in a month (one line per project). */
export const lineKeyForProject = (month: ImputacionMonth, projectId: number): string | undefined =>
    month.lines.find((l) => l.redmine_project_id === projectId)?.key

/** Results indexed by `${lineKey}|${iso}`, for the ✓/⚠ marks on the cells. */
export function resultsByCell(month: ImputacionMonth, results: SubmitResult[]): Record<string, SubmitResult> {
    const out: Record<string, SubmitResult> = {}
    for (const r of results) {
        const key = lineKeyForProject(month, r.project_id)
        if (key) out[`${key}|${r.spent_on}`] = r
    }
    return out
}

/** Days Redmine changed under the person's feet: their header says "Revisar". */
export const conflictDays = (results: SubmitResult[]): Set<IsoDate> =>
    new Set(results.filter((r) => r.status === 'conflict').map((r) => r.spent_on))

const DONE = new Set(['created', 'updated', 'deleted', 'unchanged'])

/**
 * The draft after a submit, against the month read again from Redmine:
 * - cells that went through are dropped, and so are conflicts — Redmine changed
 *   that day, so the person re-enters the value on the new figures; a blind
 *   resend of the old target cannot happen;
 * - error and not_attempted cells stay, to be fixed or sent again;
 * - every other cell (unconfirmed, or no answer at all after a timeout) stays
 *   only while the fresh read still differs from its target.
 * Cells of lines or days that are no longer editable are dropped.
 */
export function mergeAfterSubmit(draft: Draft, results: SubmitResult[], refreshed: ImputacionMonth): Draft {
    const byCell = new Map<string, SubmitResult>()
    for (const r of results) byCell.set(cellId(r.spent_on, r.project_id), r)
    // A re-read that came back read-only (the Redmine login was lost mid-run,
    // writes switched off) says nothing about what can be logged later: the
    // login banner promises the draft back after logging in, so only what
    // settled is dropped then, never cells for being "not editable" right now.
    const strict = refreshed.can_edit
    const editable = new Set(refreshed.days.filter((d) => d.editable).map((d) => d.date))
    const next: Draft = {}
    for (const [key, cells] of Object.entries(draft)) {
        const line = refreshed.lines.find((l) => l.key === key)
        if (!line) {
            if (!strict) next[key] = cells
            continue
        }
        if (strict && !line.loggable) continue
        const kept: Record<IsoDate, number> = {}
        for (const [iso, target] of Object.entries(cells)) {
            if (strict && !editable.has(iso)) continue
            const r = line.redmine_project_id !== null ? byCell.get(cellId(iso, line.redmine_project_id)) : undefined
            if (r && (DONE.has(r.status) || r.status === 'conflict')) continue
            if (r && (r.status === 'error' || r.status === 'not_attempted' || r.status === 'blocked_external')) {
                kept[iso] = target
                continue
            }
            if (Math.abs(target - val(line.logged, iso)) > EPS) kept[iso] = target
        }
        if (Object.keys(kept).length) next[key] = kept
    }
    return next
}

/**
 * The draft after a run whose month could not be re-read (the session expired
 * mid-run): the cells the server settled leave, everything else stays as is.
 */
export function dropSettled(month: ImputacionMonth, draft: Draft, results: SubmitResult[]): Draft {
    const settled = new Set(
        results.filter((r) => DONE.has(r.status) || r.status === 'conflict').map((r) => cellId(r.spent_on, r.project_id)),
    )
    const next: Draft = {}
    for (const [key, cells] of Object.entries(draft)) {
        const pid = month.lines.find((l) => l.key === key)?.redmine_project_id ?? null
        const kept: Record<IsoDate, number> = {}
        for (const [iso, target] of Object.entries(cells)) {
            if (pid !== null && settled.has(cellId(iso, pid))) continue
            kept[iso] = target
        }
        if (Object.keys(kept).length) next[key] = kept
    }
    return next
}

/** Drops cells of lines the month no longer has (a project choice moved the line). */
export function pruneOrphans(month: ImputacionMonth, draft: Draft): Draft {
    const keys = new Set(month.lines.map((l) => l.key))
    return Object.fromEntries(Object.entries(draft).filter(([key]) => keys.has(key)))
}

export const isDraftEmpty = (draft: Draft): boolean => Object.values(draft).every((c) => Object.keys(c).length === 0)

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/** Icon plus text: the state never depends on colour alone. */
export function levelInfo(level: ImputacionLevel): { icon: string; text: string; tone: 'ok' | 'warn' | 'bad' | 'muted' } {
    switch (level) {
        case 'al_dia':
            return { icon: '✓', text: 'Vas al día', tone: 'ok' }
        case 'retrasado':
            return { icon: '!', text: 'Retrasado', tone: 'warn' }
        case 'critico':
            return { icon: '‼', text: 'Muy retrasado', tone: 'bad' }
        case 'posible_retraso':
            return { icon: '?', text: 'Posible retraso', tone: 'warn' }
        case 'sin_jornada':
            return { icon: '·', text: 'Jornada sin configurar', tone: 'muted' }
        case 'sin_mapear':
            return { icon: '·', text: 'Sin usuario Redmine', tone: 'muted' }
        default:
            return { icon: '·', text: 'Sin datos', tone: 'muted' }
    }
}

/** The "Previsión a fin de mes" card. */
export function previsionLine(
    totals: ImputacionTotals,
    monthOver = false,
): { main: string; sub: string; recover: string | null } {
    return {
        main: `${fmtH(totals.forecast_month)} de ${fmtH(totals.expected_month)}`,
        sub: monthOver ? 'mes cerrado' : 'si imputas lo recomendado desde hoy',
        // The forecast only adds what is still ahead; past gaps stay gaps unless recovered.
        recover:
            !monthOver && totals.missing_to_date > 0
                ? `Para cerrar completo recupera también ${fmtH(totals.missing_to_date)} pendientes.`
                : null,
    }
}

/** "Laboratorio IA 150 h · Vacaciones 8,5 h" (lines with nothing forecast are left out). */
export function forecastByProject(month: ImputacionMonth): string {
    return orderedLines(month)
        .filter((l) => round2(l.summary.forecast_month) > 0)
        .map((l) => `${l.label} ${fmtH(l.summary.forecast_month)}`)
        .join(' · ')
}

// ---------------------------------------------------------------------------
// Draft persistence
// ---------------------------------------------------------------------------

const draftKey = (uid: number, year: number, month: number) =>
    `imputacion.draft.${uid}.${year}-${String(month).padStart(2, '0')}`

/**
 * The unsent draft survives a reload or a re-login in sessionStorage (this tab
 * only) for at most 12 h — after that the figures it was built on are stale.
 * Storage can be missing or throw (private mode, quota): the page then simply
 * works without it.
 */
export const draftStorage = {
    /** `seen` is the draft's basis (see `rebaseSeen`), kept so a restored draft still trips the day guard. */
    save(uid: number, year: number, month: number, draft: Draft, seen: SeenTotals = {}): void {
        try {
            if (isDraftEmpty(draft)) {
                window.sessionStorage.removeItem(draftKey(uid, year, month))
                return
            }
            window.sessionStorage.setItem(draftKey(uid, year, month), JSON.stringify({ saved_at: Date.now(), draft, seen }))
        } catch {
            // Storage unavailable: the draft just won't survive a reload.
        }
    },
    load(uid: number, year: number, month: number): Draft | null {
        return draftStorage.loadEntry(uid, year, month)?.draft ?? null
    },
    /** The draft and its basis; an entry saved without a basis gives `{}` (the fresh read is used then). */
    loadEntry(uid: number, year: number, month: number): { draft: Draft; seen: SeenTotals } | null {
        try {
            const raw = window.sessionStorage.getItem(draftKey(uid, year, month))
            if (!raw) return null
            const parsed = JSON.parse(raw) as { saved_at?: number; draft?: unknown; seen?: unknown }
            if (typeof parsed?.saved_at !== 'number' || Date.now() - parsed.saved_at > DRAFT_TTL_MS) {
                window.sessionStorage.removeItem(draftKey(uid, year, month))
                return null
            }
            const draft = sanitizeDraft(parsed.draft)
            if (!draft || isDraftEmpty(draft)) return null
            return { draft, seen: sanitizeSeen(parsed.seen) }
        } catch {
            return null
        }
    },
    clear(uid: number, year: number, month: number): void {
        try {
            window.sessionStorage.removeItem(draftKey(uid, year, month))
        } catch {
            // Nothing to clear when storage is unavailable.
        }
    },
}

/** Keeps only well-formed {key: {iso: number}} entries from storage. */
function sanitizeDraft(raw: unknown): Draft | null {
    if (!raw || typeof raw !== 'object') return null
    const out: Draft = {}
    for (const [key, cells] of Object.entries(raw as Record<string, unknown>)) {
        if (!cells || typeof cells !== 'object') continue
        const kept: Record<IsoDate, number> = {}
        for (const [iso, v] of Object.entries(cells as Record<string, unknown>)) {
            if (/^\d{4}-\d{2}-\d{2}$/.test(iso) && typeof v === 'number' && Number.isFinite(v)) kept[iso] = v
        }
        if (Object.keys(kept).length) out[key] = kept
    }
    return out
}

function sanitizeSeen(raw: unknown): SeenTotals {
    const out: SeenTotals = {}
    if (!raw || typeof raw !== 'object') return out
    for (const [iso, v] of Object.entries(raw as Record<string, unknown>)) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(iso) && typeof v === 'number' && Number.isFinite(v)) out[iso] = v
    }
    return out
}

/** Where the login page sends the person back after "Iniciar sesión" (internal path only). */
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'
